import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download } from "lucide-react";

const TEMPLATES = [
  { id: "classic", name: "经典黑金" },
  { id: "goldtype", name: "纯金大字" },
  { id: "qrhero", name: "扫码直达" },
];

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function baseCanvas() {
  const canvas = document.createElement("canvas");
  canvas.width = 900;
  canvas.height = 1500;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#070709";
  ctx.fillRect(0, 0, 900, 1500);
  return { canvas, ctx };
}

export function goldGradient(ctx) {
  const g = ctx.createLinearGradient(150, 0, 750, 0);
  g.addColorStop(0, "#F3E5AB");
  g.addColorStop(0.5, "#D4AF37");
  g.addColorStop(1, "#AA7C11");
  return g;
}

function drawFrame(ctx) {
  ctx.strokeStyle = "rgba(212,175,55,0.55)";
  ctx.lineWidth = 2;
  ctx.strokeRect(38, 38, 824, 1424);
  ctx.strokeStyle = "rgba(212,175,55,0.2)";
  ctx.lineWidth = 1;
  ctx.strokeRect(52, 52, 796, 1396);
}

export function drawGlow(ctx, y = 380, color = "212,175,55") {
  const glow = ctx.createRadialGradient(450, y, 60, 450, y, 780);
  glow.addColorStop(0, `rgba(${color},0.28)`);
  glow.addColorStop(1, `rgba(${color},0)`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 900, 1500);
}

function drawFooter(ctx) {
  ctx.fillStyle = "#4B5563";
  ctx.font = "22px 'Noto Sans SC', sans-serif";
  ctx.fillText("合赢项目交流社区 · 致力于为成员提供可靠、零风险的赚钱项目", 450, 1405);
}

export function drawQrCard(ctx, qrImg, cx, cy, size) {
  const pad = size * 0.08;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(cx - size / 2, cy - size / 2, size, size);
  ctx.strokeStyle = "rgba(212,175,55,0.8)";
  ctx.lineWidth = 3;
  ctx.strokeRect(cx - size / 2 - 14, cy - size / 2 - 14, size + 28, size + 28);
  ctx.drawImage(qrImg, cx - size / 2 + pad, cy - size / 2 + pad, size - pad * 2, size - pad * 2);
}

function drawClassic(ctx, qrImg, inviteCode) {
  const gold = goldGradient(ctx);
  drawGlow(ctx);
  drawFrame(ctx);
  ctx.textAlign = "center";
  ctx.fillStyle = gold;
  ctx.font = "900 74px 'Noto Serif SC', serif";
  ctx.fillText("合赢项目交流社区", 450, 215);
  ctx.fillStyle = "#8b8f98";
  ctx.font = "22px 'JetBrains Mono', monospace";
  ctx.fillText("HEYING PROJECT EXCHANGE COMMUNITY", 450, 265);
  ctx.strokeStyle = "rgba(212,175,55,0.7)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(280, 310);
  ctx.lineTo(620, 310);
  ctx.stroke();
  ctx.fillStyle = "#F8F9FA";
  ctx.font = "700 44px 'Noto Serif SC', serif";
  ctx.fillText("汇聚项目资源 · 连接可靠项目", 450, 405);
  ctx.fillStyle = "#9CA3AF";
  ctx.font = "26px 'Noto Sans SC', sans-serif";
  ctx.fillText("致力于为成员提供可靠、零风险的赚钱项目", 450, 465);
  ctx.fillStyle = gold;
  ctx.font = "700 34px 'Noto Serif SC', serif";
  ctx.fillText("四重专业体系层层把关", 450, 545);
  ctx.fillText("只交付经得起验证的机会", 450, 598);
  drawQrCard(ctx, qrImg, 450, 880, 360);
  ctx.fillStyle = gold;
  ctx.font = "700 60px 'JetBrains Mono', monospace";
  ctx.fillText(inviteCode, 450, 1200);
  drawFooter(ctx);
}

function drawGoldType(ctx, qrImg, inviteCode) {
  const gold = goldGradient(ctx);
  drawGlow(ctx, 500);
  drawFrame(ctx);
  ctx.textAlign = "center";
  ctx.fillStyle = gold;
  ctx.font = "900 300px 'Noto Serif SC', serif";
  ctx.fillText("合赢", 450, 420);
  ctx.font = "900 84px 'Noto Serif SC', serif";
  ctx.fillText("项目交流社区", 450, 560);
  ctx.fillStyle = "#8b8f98";
  ctx.font = "20px 'JetBrains Mono', monospace";
  ctx.fillText("HEYING PROJECT EXCHANGE COMMUNITY", 450, 615);
  ctx.fillStyle = "#F8F9FA";
  ctx.font = "700 38px 'Noto Serif SC', serif";
  ctx.fillText("汇聚项目资源 · 连接可靠项目", 450, 715);
  ctx.fillStyle = "#9CA3AF";
  ctx.font = "25px 'Noto Sans SC', sans-serif";
  ctx.fillText("致力于为成员提供可靠、零风险的赚钱项目", 450, 768);
  ctx.fillText("四重专业体系层层把关 · 只交付经得起验证的机会", 450, 810);
  ctx.fillStyle = gold;
  ctx.font = "700 92px 'JetBrains Mono', monospace";
  ctx.fillText(inviteCode, 450, 985);
  drawQrCard(ctx, qrImg, 450, 1210, 230);
}

function drawQrHero(ctx, qrImg, inviteCode) {
  const gold = goldGradient(ctx);
  drawGlow(ctx, 620);
  drawFrame(ctx);
  ctx.textAlign = "center";
  ctx.fillStyle = gold;
  ctx.font = "900 62px 'Noto Serif SC', serif";
  ctx.fillText("合赢项目交流社区", 450, 180);
  ctx.fillStyle = "#F8F9FA";
  ctx.font = "700 34px 'Noto Serif SC', serif";
  ctx.fillText("汇聚项目资源 · 连接可靠项目", 450, 250);
  drawQrCard(ctx, qrImg, 450, 660, 560);
  ctx.fillStyle = "#9CA3AF";
  ctx.font = "25px 'Noto Sans SC', sans-serif";
  ctx.fillText("致力于为成员提供可靠、零风险的赚钱项目", 450, 1090);
  ctx.fillText("四重专业体系层层把关 · 只交付经得起验证的机会", 450, 1137);
  ctx.fillStyle = gold;
  ctx.font = "700 56px 'JetBrains Mono', monospace";
  ctx.fillText(inviteCode, 450, 1250);
  drawFooter(ctx);
}

export default function InvitePoster({ open, onClose, inviteLink, inviteCode }) {
  const [tpl, setTpl] = useState("classic");
  const [imgUrl, setImgUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setImgUrl(null);
      generate(tpl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tpl]);

  const generate = async (template) => {
    setBusy(true);
    try {
      await document.fonts.ready;
      const qrData = await QRCode.toDataURL(inviteLink, {
        width: 600,
        margin: 0,
        color: { dark: "#0A0A0E", light: "#FFFFFF" },
      });
      const qrImg = await loadImage(qrData);
      const { canvas, ctx } = baseCanvas();
      if (template === "goldtype") drawGoldType(ctx, qrImg, inviteCode);
      else if (template === "qrhero") drawQrHero(ctx, qrImg, inviteCode);
      else drawClassic(ctx, qrImg, inviteCode);
      setImgUrl(canvas.toDataURL("image/png"));
    } catch (e) {
      toast.error("海报生成失败，请重试");
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `合赢邀请海报-${inviteCode}-${tpl}.png`;
    a.click();
    toast.success("海报已保存，快去朋友圈分享吧");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-[#0F0F14] border border-[#D4AF37]/25 text-[#F8F9FA] sm:max-w-md"
        data-testid="poster-dialog"
      >
        <DialogTitle className="font-serif-sc text-xl tracking-widest text-center">专属邀请海报</DialogTitle>
        <DialogDescription className="sr-only">会员专属邀请海报，含品牌使命文案、邀请二维码与邀请码</DialogDescription>
        <div className="grid grid-cols-3 gap-2">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              data-testid={`poster-tpl-${t.id}`}
              onClick={() => setTpl(t.id)}
              className={`py-2 text-xs tracking-widest border transition-colors ${
                tpl === t.id ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#D4AF37]/15 text-gray-500 hover:text-gray-300"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>
        <div className="grid place-items-center py-2 min-h-40">
          {busy && <p className="text-sm text-gray-500 py-20">海报绘制中...</p>}
          {!busy && imgUrl && (
            <img
              src={imgUrl}
              data-testid="poster-image"
              alt="合赢专属邀请海报"
              className="w-full max-w-[300px] border border-[#D4AF37]/25"
            />
          )}
        </div>
        <button
          data-testid="poster-download-btn"
          onClick={download}
          disabled={!imgUrl}
          className="btn-gold py-2.5 text-sm w-full inline-flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" /> 保存海报到本地
        </button>
      </DialogContent>
    </Dialog>
  );
}
